<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import GupshupTemplatesAPI from 'dashboard/api/gupshupTemplates';

const DEFAULT_GUPSHUP_TEMPLATE_FORM = {
  elementName: '',
  languageCode: 'pt_BR',
  category: 'UTILITY',
  templateType: 'TEXT',
  vertical: 'HEALTHCARE',
  content: '',
  footer: '',
  buttons: '',
  example: '',
  enableSample: true,
};

export default {
  components: {
    SettingsSection,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      templates: [],
      showCreateForm: false,
      loading: false,
      isEditing: false,
      currentTemplateId: null,
      currentTemplateChatwootId: null,
      connecting: false,
      activeCredentials: {
        appId: '',
        token: '',
        accountApiKey: '',
      },
      connectionState: 'disconnected',
      disconnecting: false,
      suppressStateWatcher: false,
      credentials: {
        appId: '',
        token: '',
        accountApiKey: '',
      },
      connectedAppName: '',
      connectedPhoneNumber: '',
      formData: { ...DEFAULT_GUPSHUP_TEMPLATE_FORM },
      iungoSettings: {
        sendTypingIndicator: false,
        autoMarkRead: false,
      },
      savingIungoSettings: false,
      categories: [
        { value: 'UTILITY', label: 'Utilitário' },
        { value: 'MARKETING', label: 'Marketing' },
        { value: 'AUTHENTICATION', label: 'Autenticação' },
      ],
      templateTypes: [
        { value: 'TEXT', label: 'Texto' },
        { value: 'MEDIA', label: 'Mídia' },
        { value: 'INTERACTIVE', label: 'Interativo' },
      ],
      verticals: [
        { value: 'HEALTHCARE', label: 'Saúde' },
        { value: 'BANKING', label: 'Banco' },
        { value: 'RETAIL', label: 'Varejo' },
        { value: 'EDUCATION', label: 'Educação' },
        { value: 'TRAVEL', label: 'Viagem' },
        { value: 'UTILITIES', label: 'Utilidades' },
      ],
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    isGupshupChannel() {
      return this.inbox?.channel_type === 'Channel::Iungo';
    },
    isConnected() {
      return this.connectionState === 'connected';
    },
    hasPendingCredentials() {
      return Boolean(
        this.credentials.appId?.trim() &&
          this.credentials.token?.trim() &&
          this.credentials.accountApiKey?.trim()
      );
    },
    canConnect() {
      if (['connecting', 'disconnecting'].includes(this.connectionState)) {
        return false;
      }

      if (this.hasPendingCredentials) {
        return true;
      }

      return this.credentialsMatchActive();
    },
    connectionStatusLabel() {
      if (this.connectionState === 'connecting') {
        return 'Conectando…';
      }
      if (this.connectionState === 'disconnecting') {
        return 'Desconectando…';
      }
      if (this.connectionState === 'pending') {
        return 'Atualização pendente';
      }
      return this.isConnected ? 'Conectado' : 'Desconectado';
    },
    connectionStatusClass() {
      if (this.connectionState === 'connecting') {
        return 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200';
      }
      if (this.connectionState === 'disconnecting') {
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200';
      }
      if (this.connectionState === 'pending') {
        return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200';
      }
      return this.isConnected
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200'
        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
    },
    connectedPhoneDisplay() {
      if (!this.connectedPhoneNumber) {
        return '';
      }
      const trimmed = this.connectedPhoneNumber.trim();
      if (!trimmed) {
        return '';
      }
      return trimmed.startsWith('+') ? trimmed : `+${trimmed}`;
    },
    hasConnectedMetadata() {
      return (
        this.isConnected &&
        (this.connectedAppName || this.connectedPhoneDisplay)
      );
    },
    webhookUrl() {
      return this.inbox?.webhook_url || '';
    },
    connectionStatusDotClass() {
      if (this.connectionState === 'connecting') {
        return 'bg-sky-500';
      }
      if (this.connectionState === 'disconnecting') {
        return 'bg-amber-500';
      }
      if (this.connectionState === 'pending') {
        return 'bg-indigo-500';
      }
      return this.isConnected
        ? 'bg-emerald-500'
        : 'bg-slate-400 dark:bg-slate-500';
    },
  },
  watch: {
    'credentials.appId': 'handleCredentialsChange',
    'credentials.token': 'handleCredentialsChange',
    'credentials.accountApiKey': 'handleCredentialsChange',
    inbox: {
      handler: 'setIungoSettingsFromInbox',
      immediate: true,
    },
  },
  async mounted() {
    await this.loadConfiguration();
    await this.fetchTemplates();
  },
  methods: {
    setIungoSettingsFromInbox() {
      const attrs = this.inbox?.additional_attributes || {};
      const typingValue = attrs.gupshup_send_typing_indicator;
      const readValue = attrs.gupshup_auto_mark_read;
      this.iungoSettings.sendTypingIndicator =
        typingValue === true || typingValue === 'true';
      this.iungoSettings.autoMarkRead =
        readValue === true || readValue === 'true';
    },
    async updateIungoSettings() {
      if (!this.isGupshupChannel) {
        return;
      }

      this.savingIungoSettings = true;
      try {
        const currentAttributes = this.inbox?.additional_attributes || {};
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: {
            additional_attributes: {
              ...currentAttributes,
              gupshup_send_typing_indicator:
                this.iungoSettings.sendTypingIndicator,
              gupshup_auto_mark_read: this.iungoSettings.autoMarkRead,
            },
          },
        });
        useAlert('Configurações da Iungo atualizadas.');
      } catch (error) {
        useAlert('Não foi possível atualizar as configurações da Iungo.');
      } finally {
        this.savingIungoSettings = false;
      }
    },
    buildTemplatePayloadFromRemote(template = {}) {
      if (!template) {
        return null;
      }

      const elementName =
        template.elementName || template.element_name || template.name;
      if (!elementName) {
        return null;
      }

      const templateId =
        template.templateId || template.id || template.gupshupTemplateId;
      const languageCode = template.languageCode || template.language;
      const templateType = template.templateType || template.type;
      const components = template.components || [];
      const bodyComponent = components.find(
        component => component.type === 'BODY'
      );
      const content =
        (bodyComponent && bodyComponent.text) ||
        template.content ||
        template.data ||
        '';

      return {
        gupshup_template_id: templateId,
        templateId,
        element_name: elementName,
        elementName,
        language_code: languageCode,
        languageCode,
        category: template.category,
        template_type: templateType,
        templateType,
        vertical: template.vertical,
        status: template.status,
        quality: template.quality,
        content,
        data: content,
        footer: template.footer,
        example: template.example,
        enable_sample: template.enableSample,
        enableSample: template.enableSample,
        buttons: template.buttons,
        container_meta: template.containerMeta,
        containerMeta: template.containerMeta,
        meta: template.meta,
        namespace: template.namespace,
        components,
        raw_payload: template,
        rawPayload: template,
        provider: template.provider || 'iungo',
      };
    },
    async syncExistingTemplates(storedTemplates = []) {
      if (!this.isConnected || !storedTemplates.length) {
        return;
      }

      try {
        const response = await this.callGupshupAPI('GET', '/templates');
        if (response.status !== 'success') {
          throw new Error(
            response.message ||
              'Não foi possível sincronizar os templates da Iungo'
          );
        }

        const remoteTemplates = response.templates || [];
        if (!remoteTemplates.length) {
          return;
        }

        const remoteByElement = new Map();
        remoteTemplates.forEach(template => {
          const elementName = (
            template.elementName ||
            template.element_name ||
            template.name ||
            ''
          ).toLowerCase();
          if (elementName) {
            remoteByElement.set(elementName, template);
          }
        });

        for (const storedTemplate of storedTemplates) {
          const elementName = (
            storedTemplate.element_name ||
            storedTemplate.elementName ||
            storedTemplate.name ||
            ''
          ).toLowerCase();
          if (!elementName || !remoteByElement.has(elementName)) {
            continue;
          }

          const remoteTemplate = remoteByElement.get(elementName);
          const payload = this.buildTemplatePayloadFromRemote(remoteTemplate);
          if (!payload) {
            continue;
          }

          const chatwootId = storedTemplate.chatwootId || storedTemplate.id;
          if (!chatwootId) {
            continue;
          }

          const currentStatus = (
            storedTemplate.status ||
            storedTemplate.Status ||
            ''
          ).toLowerCase();
          const nextStatus = (payload.status || '').toLowerCase();
          if (currentStatus === nextStatus) {
            continue;
          }

          await GupshupTemplatesAPI.update(this.inbox.id, chatwootId, payload);
        }
      } catch (error) {
        console.error(
          'Erro ao sincronizar templates existentes com Iungo:',
          error
        );
      }
    },
    async loadConfiguration() {
      try {
        const response = await GupshupTemplatesAPI.fetchConfiguration(
          this.inbox.id
        );
        const config = response.data?.gupshup_configuration;
        if (config) {
          this.suppressStateWatcher = true;
          this.credentials.appId = (config.app_id || '').trim();
          this.credentials.token = (config.token || '').trim();
          this.credentials.accountApiKey = (
            config.account_api_key ||
            config.api_key ||
            ''
          ).trim();
          this.activeCredentials.appId = this.credentials.appId;
          this.activeCredentials.token = this.credentials.token;
          this.activeCredentials.accountApiKey = this.credentials.accountApiKey;
          this.connectedAppName = config.app_name || '';
          this.connectedPhoneNumber = config.phone_number || '';
          this.$nextTick(() => {
            this.suppressStateWatcher = false;
            this.connectionState = this.credentialsMatchActive()
              ? 'connected'
              : this.hasPendingCredentials
              ? 'pending'
              : 'disconnected';
          });
          this.connectionState = 'connected';
        } else {
          this.suppressStateWatcher = true;
          this.credentials.appId = '';
          this.credentials.token = '';
          this.credentials.accountApiKey = '';
          this.activeCredentials.appId = '';
          this.activeCredentials.token = '';
          this.activeCredentials.accountApiKey = '';
          this.connectedAppName = '';
          this.connectedPhoneNumber = '';
          this.$nextTick(() => {
            this.suppressStateWatcher = false;
            this.connectionState = this.credentialsMatchActive()
              ? 'connected'
              : this.hasPendingCredentials
              ? 'pending'
              : 'disconnected';
          });
        }
      } catch (error) {
        console.error('Erro ao carregar configuração da Iungo:', error);
        this.suppressStateWatcher = true;
        this.credentials.appId = '';
        this.credentials.token = '';
        this.credentials.accountApiKey = '';
        this.activeCredentials.appId = '';
        this.activeCredentials.token = '';
        this.activeCredentials.accountApiKey = '';
        this.connectedAppName = '';
        this.connectedPhoneNumber = '';
        this.$nextTick(() => {
          this.suppressStateWatcher = false;
          this.connectionState = this.credentialsMatchActive()
            ? 'connected'
            : this.hasPendingCredentials
            ? 'pending'
            : 'disconnected';
        });
      }
    },
    async connectGupshup() {
      if (!this.hasPendingCredentials) {
        useAlert(
          'Informe o App ID, o Token e o Account API Key antes de conectar.'
        );
        return;
      }

      this.connecting = true;
      this.connectionState = 'connecting';
      try {
        const payload = {
          app_id: this.credentials.appId.trim(),
          token: this.credentials.token.trim(),
          account_api_key: this.credentials.accountApiKey.trim(),
        };
        payload.api_key = payload.account_api_key;

        const response = await GupshupTemplatesAPI.connect(
          this.inbox.id,
          payload
        );
        const config = response.data?.gupshup_configuration;
        if (config) {
          this.suppressStateWatcher = true;
          this.credentials.appId = (config.app_id || '').trim();
          this.credentials.token = (config.token || '').trim();
          this.credentials.accountApiKey = (
            config.account_api_key ||
            config.api_key ||
            ''
          ).trim();
          this.activeCredentials.appId = this.credentials.appId;
          this.activeCredentials.token = this.credentials.token;
          this.activeCredentials.accountApiKey = this.credentials.accountApiKey;
          this.connectedAppName = config.app_name || '';
          this.connectedPhoneNumber = config.phone_number || '';
          this.$nextTick(() => {
            this.suppressStateWatcher = false;
            this.connectionState = 'connected';
          });
        }

        await this.fetchTemplates({ force: true });
        useAlert('Conexão com Iungo realizada com sucesso!');
        this.connectionState = 'connected';
      } catch (error) {
        console.error('Erro ao conectar a Iungo:', error);
        const message =
          error.response?.data?.message ||
          error.message ||
          'Não foi possível conectar a Iungo.';
        useAlert(message);
        this.connectionState = this.credentialsMatchActive()
          ? 'connected'
          : this.hasPendingCredentials
          ? 'pending'
          : 'disconnected';
      } finally {
        this.connecting = false;
        if (this.connectionState === 'connecting') {
          this.connectionState = 'disconnected';
        }
      }
    },
    async disconnectGupshup() {
      if (!this.isConnected) {
        this.suppressStateWatcher = true;
        this.credentials.appId = '';
        this.credentials.token = '';
        this.credentials.accountApiKey = '';
        this.activeCredentials.appId = '';
        this.activeCredentials.token = '';
        this.activeCredentials.accountApiKey = '';
        this.connectedAppName = '';
        this.connectedPhoneNumber = '';
        this.$nextTick(() => {
          this.suppressStateWatcher = false;
          this.connectionState = 'disconnected';
        });
        return;
      }

      this.disconnecting = true;
      this.connectionState = 'disconnecting';
      const previousCredentials = { ...this.activeCredentials };
      const previousMetadata = {
        appName: this.connectedAppName,
        phoneNumber: this.connectedPhoneNumber,
      };
      try {
        await GupshupTemplatesAPI.disconnect(this.inbox.id);
        this.suppressStateWatcher = true;
        this.credentials.appId = '';
        this.credentials.token = '';
        this.credentials.accountApiKey = '';
        this.activeCredentials.appId = '';
        this.activeCredentials.token = '';
        this.activeCredentials.accountApiKey = '';
        this.connectedAppName = '';
        this.connectedPhoneNumber = '';
        this.$nextTick(() => {
          this.suppressStateWatcher = false;
          this.connectionState = 'disconnected';
        });
        this.templates = [];
        useAlert('Conexão com Iungo removida.');
      } catch (error) {
        console.error('Erro ao desconectar da Iungo:', error);
        const message =
          error.response?.data?.message ||
          error.message ||
          'Não foi possível desconectar.';
        useAlert(message);
        this.suppressStateWatcher = true;
        this.credentials.appId = previousCredentials.appId || '';
        this.credentials.token = previousCredentials.token || '';
        this.credentials.accountApiKey =
          previousCredentials.accountApiKey || '';
        this.activeCredentials.appId = previousCredentials.appId || '';
        this.activeCredentials.token = previousCredentials.token || '';
        this.activeCredentials.accountApiKey =
          previousCredentials.accountApiKey || '';
        this.connectedAppName = previousMetadata.appName || '';
        this.connectedPhoneNumber = previousMetadata.phoneNumber || '';
        this.$nextTick(() => {
          this.suppressStateWatcher = false;
          this.connectionState = this.credentialsMatchActive()
            ? 'connected'
            : this.hasPendingCredentials
            ? 'pending'
            : 'disconnected';
        });
      } finally {
        this.disconnecting = false;
      }
    },
    handleCredentialsChange() {
      if (this.suppressStateWatcher) {
        return;
      }

      if (['connecting', 'disconnecting'].includes(this.connectionState)) {
        return;
      }

      const appId = this.credentials.appId.trim();
      const token = this.credentials.token.trim();
      const accountKey = this.credentials.accountApiKey.trim();

      if (!appId && !token && !accountKey) {
        this.connectionState = 'disconnected';
        return;
      }

      if (this.credentialsMatchActive()) {
        this.connectionState = 'connected';
        return;
      }

      this.connectionState = 'pending';
    },
    credentialsMatchActive() {
      return (
        Boolean(this.activeCredentials.appId) &&
        Boolean(this.activeCredentials.token) &&
        Boolean(this.activeCredentials.accountApiKey) &&
        this.credentials.appId.trim() === this.activeCredentials.appId &&
        this.credentials.token.trim() === this.activeCredentials.token &&
        this.credentials.accountApiKey.trim() ===
          this.activeCredentials.accountApiKey
      );
    },
    async testApiConnection() {
      if (!this.ensureCredentialsConfigured()) {
        return;
      }
      console.log('Testando conexão com API da Iungo...');
      console.log('Configurações do canal:', {
        provider: this.inbox.provider,
        provider_config: this.inbox.provider_config,
        channel_type: this.inbox.channel_type,
      });
    },
    getGupshupAppId() {
      const appId = this.activeCredentials.appId;
      if (!appId) {
        throw new Error('App ID da Iungo não configurado.');
      }
      return appId;
    },
    getGupshupToken() {
      const token = this.activeCredentials.token;
      if (!token) {
        throw new Error('Token da Iungo não configurado.');
      }
      return token;
    },
    getGupshupAccountApiKey() {
      const accountKey = this.activeCredentials.accountApiKey;
      if (!accountKey) {
        throw new Error('Account API Key da Iungo não configurado.');
      }
      return accountKey;
    },
    translatedCategory(category) {
      const map = {
        UTILITY: 'Utilitário',
        MARKETING: 'Marketing',
        AUTHENTICATION: 'Autenticação',
      };
      return map[category] || category;
    },
    translatedTemplateType(type) {
      const map = {
        TEXT: 'Texto',
        MEDIA: 'Mídia',
        INTERACTIVE: 'Interativo',
      };
      return map[type] || type;
    },
    translatedLanguage(code) {
      const map = {
        pt_BR: 'Português (Brasil)',
        en_US: 'Inglês (EUA)',
        es_ES: 'Espanhol (Espanha)',
      };
      return map[code] || code;
    },
    translatedStatus(status) {
      if (!status) return '';
      const map = {
        APPROVED: 'Aprovado',
        PENDING: 'Pendente',
        REJECTED: 'Rejeitado',
        IN_REVIEW: 'Em análise',
        PAUSED: 'Pausado',
        DISABLED: 'Desativado',
        SENT_FOR_REVIEW: 'Enviado para revisão',
      };
      return map[status.toUpperCase()] || status;
    },
    translatedQuality(quality) {
      if (!quality) return '';
      const map = {
        GREEN: 'Alta',
        YELLOW: 'Média',
        RED: 'Baixa',
        UNKNOWN: 'Desconhecida',
      };
      return map[quality.toUpperCase()] || quality;
    },
    adjustPayloadForGupshup(payload) {
      // Ajustar payload para ser mais compatível com a API da Iungo
      const adjustedPayload = { ...payload };

      // Remover apenas campos completamente vazios (não modificar dados do usuário)
      Object.keys(adjustedPayload).forEach(key => {
        if (
          adjustedPayload[key] === null ||
          adjustedPayload[key] === undefined
        ) {
          delete adjustedPayload[key];
        }
      });

      // NÃO modificar dados que o usuário forneceu - apenas adicionar se estiverem completamente ausentes
      // NÃO adicionar footer automaticamente - deixar como o usuário escolheu
      // if (!('footer' in adjustedPayload)) {
      //   adjustedPayload.footer = 'Obrigado por utilizar nossos serviços.';
      // }

      // NÃO adicionar botões automaticamente - deixar como o usuário escolheu
      // Só adicionar se o campo estiver completamente ausente E o usuário não preencheu nada
      // if (!('buttons' in adjustedPayload) && adjustedPayload.category === 'UTILITY') {
      //   adjustedPayload.buttons = '[{"type":"QUICK_REPLY","text":"Mudar Data/Hora"}]';
      // }

      // NÃO modificar o tipo de template - deixar como o usuário escolheu
      // NÃO modificar o nome do elemento - deixar como o usuário digitou
      // NÃO modificar o conteúdo - deixar como o usuário digitou

      console.log(
        'Payload ajustado para Iungo (apenas campos ausentes adicionados):',
        adjustedPayload
      );
      return adjustedPayload;
    },
    async createSimpleTemplate() {
      if (!this.ensureCredentialsConfigured()) {
        return;
      }
      this.loading = true;
      try {
        const appId = this.getGupshupAppId();
        // Criar um template simples e compatível (usando exatamente os dados do curl que funciona)
        const simplePayload = {
          appId,
          elementName: 'confirmacao_agendamento_' + Date.now(),
          languageCode: 'pt_BR',
          category: 'UTILITY',
          templateType: 'TEXT',
          vertical: 'HEALTHCARE',
          content: 'Olá, seu agendamento para o dia {{1}} foi confirmado.',
          footer: 'Obrigado por utilizar nossos serviços.',
          buttons: '[{"type":"QUICK_REPLY","text":"Mudar Data/Hora"}]',
          example:
            'Olá, seu agendamento para o dia 25/11 às 14:00 foi confirmado.',
          enableSample: true,
        };

        console.log('Criando template simples:', simplePayload);

        const response = await this.callGupshupAPI(
          'POST',
          '/templates',
          simplePayload
        );
        if (response.status !== 'success') {
          throw new Error(response.message || 'Erro ao criar template simples');
        }

        await this.persistTemplateLocally({
          elementName: simplePayload.elementName,
          templateId: response.templateId || response.id,
        });
        await this.fetchTemplates();

        useAlert('Template simples criado com sucesso!');
      } catch (error) {
        console.error('Erro ao criar template simples:', error);
        useAlert(
          'Erro ao criar template simples: ' +
            (error.message || 'Ocorreu um erro desconhecido.')
        );
      } finally {
        this.loading = false;
      }
    },
    async createTemplateWithoutAdjustment() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      if (!this.ensureCredentialsConfigured()) {
        return;
      }

      this.loading = true;
      try {
        const appId = this.getGupshupAppId();
        // Criar payload sem ajustes automáticos
        const payload = {
          appId,
          elementName: this.formData.elementName,
          languageCode: this.formData.languageCode,
          category: this.formData.category,
          templateType: this.formData.templateType,
          vertical: this.formData.vertical,
          content: this.formData.content,
          footer: this.formData.footer,
          example: this.formData.example,
          enableSample: this.formData.enableSample,
        };

        // Adicionar botões apenas se fornecidos
        if (this.formData.buttons && this.formData.buttons.trim()) {
          try {
            JSON.parse(this.formData.buttons); // Validar JSON
            payload.buttons = this.formData.buttons;
          } catch (error) {
            throw new Error('Formato de botões inválido. Use JSON válido.');
          }
        }

        console.log('Payload SEM ajustes:', payload);

        // Criar template via API da Iungo SEM ajustes
        const response = await this.callGupshupAPI(
          'POST',
          '/templates',
          payload
        );
        if (response.status !== 'success') {
          throw new Error(response.message || 'Erro ao criar template');
        }

        await this.persistTemplateLocally({
          elementName: payload.elementName,
          templateId: response.templateId || response.id,
        });
        await this.fetchTemplates();

        useAlert('Template criado com sucesso!');
        this.resetForm();
      } catch (error) {
        console.error('Erro ao criar template sem ajustes:', error);
        useAlert(
          'Erro ao criar template: ' +
            (error.message || 'Ocorreu um erro desconhecido.')
        );
      } finally {
        this.loading = false;
      }
    },
    async fetchTemplates({ force = false } = {}) {
      if (!force && !this.isConnected) {
        this.templates = [];
        return;
      }

      this.loading = true;
      try {
        let storedTemplates = await this.loadStoredTemplates();

        if (this.isConnected && storedTemplates.length) {
          await this.syncExistingTemplates(storedTemplates);
          storedTemplates = await this.loadStoredTemplates();
        }

        this.templates = storedTemplates;
      } catch (error) {
        console.error('Erro ao carregar templates armazenados:', error);
        useAlert(
          'Erro ao carregar templates: ' +
            (error.message || 'Ocorreu um erro desconhecido.')
        );
      } finally {
        this.loading = false;
      }
    },
    async loadStoredTemplates() {
      const response = await GupshupTemplatesAPI.list(this.inbox.id);
      return response.data?.gupshup_templates || [];
    },
    async persistTemplateLocally({
      elementName,
      templateId = null,
      chatwootId = null,
    }) {
      if (!this.isConnected) {
        console.warn(
          'Credenciais ausentes, ignorando persistência local baseada na Iungo.'
        );
        return;
      }
      try {
        const templateDetails = await this.fetchTemplateDetailsFromGupshup({
          elementName,
          templateId,
        });
        if (!templateDetails) {
          console.warn(
            'Template não encontrado na API da Iungo para persistência local:',
            {
              elementName,
              templateId,
            }
          );
          return;
        }

        if (templateDetails) {
          const payload = this.buildTemplatePayloadFromRemote(templateDetails);
          if (!payload) {
            return;
          }

          if (chatwootId) {
            await GupshupTemplatesAPI.update(
              this.inbox.id,
              chatwootId,
              payload
            );
          } else {
            await GupshupTemplatesAPI.create(this.inbox.id, payload);
          }
        }
      } catch (error) {
        console.error('Erro ao persistir template localmente:', error);
      }
    },
    async fetchTemplateDetailsFromGupshup({ elementName, templateId }) {
      if (!this.isConnected) {
        return null;
      }
      try {
        await this.testApiConnection();
        const response = await this.callGupshupAPI('GET', '/templates');
        if (response.status !== 'success') {
          throw new Error(
            response.message || 'Não foi possível obter os templates da Iungo'
          );
        }

        const templates = response.templates || [];
        let template;
        if (templateId) {
          template = templates.find(
            item => item.id === templateId || item.templateId === templateId
          );
        } else {
          template = templates.find(item => item.elementName === elementName);
        }

        return this.buildTemplatePayloadFromRemote(template);
      } catch (error) {
        console.error('Erro ao buscar detalhes do template na Iungo:', error);
        return null;
      }
    },
    async createTemplate() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      if (!this.ensureCredentialsConfigured()) {
        return;
      }

      this.loading = true;
      try {
        const appId = this.getGupshupAppId();
        // Validar e ajustar dados antes de enviar
        const payload = {
          appId,
          elementName: this.formData.elementName,
          languageCode: this.formData.languageCode,
          category: this.formData.category,
          templateType: this.formData.templateType,
          vertical: this.formData.vertical,
          content: this.formData.content,
          footer: this.formData.footer || '',
          example: this.formData.example || '',
          enableSample: this.formData.enableSample,
        };

        // Validações específicas por tipo de template
        if (this.formData.templateType === 'MEDIA') {
          // Para templates de mídia, alguns campos podem ser obrigatórios
          if (
            !this.formData.content ||
            this.formData.content.trim().length === 0
          ) {
            throw new Error(
              'Para templates de mídia, o conteúdo é obrigatório.'
            );
          }
        }

        // Validar e processar botões apenas se fornecidos
        if (this.formData.buttons && this.formData.buttons.trim()) {
          try {
            // Validar se é JSON válido
            const buttonsJson = JSON.parse(this.formData.buttons);
            payload.buttons = this.formData.buttons;
          } catch (error) {
            throw new Error('Formato de botões inválido. Use JSON válido.');
          }
        }

        // Validações específicas para categoria MARKETING
        if (this.formData.category === 'MARKETING') {
          // Templates de marketing podem ter requisitos específicos
          if (
            !this.formData.example ||
            this.formData.example.trim().length === 0
          ) {
            console.warn(
              'Templates de marketing geralmente precisam de um exemplo.'
            );
          }
        }

        // Log do payload antes de enviar
        console.log('Payload original:', payload);

        // Ajustar payload para ser mais compatível com Iungo
        const adjustedPayload = this.adjustPayloadForGupshup(payload);

        // Log do payload ajustado
        console.log('Payload ajustado:', adjustedPayload);

        // Criar template via API da Iungo
        const response = await this.callGupshupAPI(
          'POST',
          '/templates',
          adjustedPayload
        );
        if (response.status !== 'success') {
          throw new Error(response.message || 'Erro ao criar template');
        }

        await this.persistTemplateLocally({
          elementName: adjustedPayload.elementName,
          templateId: response.templateId || response.id,
        });
        await this.fetchTemplates({ force: true });

        useAlert('Template criado com sucesso!');
        this.resetForm();
      } catch (error) {
        console.error('Erro ao criar template:', error);
        console.error('Detalhes do erro:', {
          message: error.message,
          status: error.status,
          response: error.response,
          stack: error.stack,
        });
        useAlert(
          'Erro ao criar template: ' +
            (error.message || 'Ocorreu um erro desconhecido.')
        );
      } finally {
        this.loading = false;
      }
    },
    async callGupshupAPI(method, endpoint, data = null) {
      const token = this.getGupshupToken();
      const accountKey = this.getGupshupAccountApiKey();
      const appId = this.getGupshupAppId();
      const baseUrl = 'https://partner.gupshup.io/partner/app';

      const url = `${baseUrl}/${appId}${endpoint}`;

      const options = {
        method,
        headers: {
          Authorization: token,
          apikey: accountKey,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      };

      if (data && method !== 'GET') {
        const formData = new URLSearchParams();
        Object.keys(data).forEach(key => {
          if (data[key] !== null && data[key] !== undefined) {
            formData.append(key, data[key]);
          }
        });
        options.body = formData;
      }

      // Log da requisição para debug
      console.log('Requisição para API da Iungo:', {
        method,
        url,
        headers: options.headers,
        body: options.body ? options.body.toString() : null,
        data: data,
      });

      const response = await fetch(url, options);

      // Log da resposta para debug
      console.log('Resposta da API da Iungo:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (parseError) {
          console.error('Erro ao fazer parse da resposta de erro:', parseError);
          errorData = {
            message: `Erro HTTP ${response.status}: ${response.statusText}`,
          };
        }

        console.error('Erro da API da Iungo:', {
          status: response.status,
          statusText: response.statusText,
          url: url,
          errorData: errorData,
          headers: Object.fromEntries(response.headers.entries()),
        });

        throw new Error(
          errorData.message ||
            `Erro na API: ${response.status} - ${response.statusText}`
        );
      }

      const responseData = await response.json();

      // Log da resposta de sucesso para debug
      console.log('Resposta de sucesso da API da Iungo:', responseData);

      return responseData;
    },
    ensureCredentialsConfigured() {
      if (this.isConnected) {
        return true;
      }
      const missing = [];
      if (!this.credentials.appId?.trim()) {
        missing.push('app_id');
      }
      if (!this.credentials.token?.trim()) {
        missing.push('token');
      }
      if (!this.credentials.accountApiKey?.trim()) {
        missing.push('account_api_key');
      }
      const labelMap = {
        app_id: 'App ID',
        token: 'Token',
        account_api_key: 'Account API Key',
      };
      const missingFields = missing
        .map(field => labelMap[field] || field)
        .join(' e ');
      useAlert(
        missingFields
          ? `Credenciais da Iungo ausentes (${missingFields}). Informe os dados e clique em "Conectar".`
          : 'Conecte-se a Iungo clicando no botão "Conectar" para habilitar o gerenciamento de templates.'
      );
      return false;
    },
    resetForm() {
      this.formData = { ...DEFAULT_GUPSHUP_TEMPLATE_FORM };
      this.showCreateForm = false;
      this.isEditing = false;
      this.currentTemplateId = null;
      this.currentTemplateChatwootId = null;
      this.v$.$reset();
    },
    openCreateTemplateModal() {
      this.isEditing = false;
      this.currentTemplateId = null;
      this.currentTemplateChatwootId = null;
      this.formData = { ...DEFAULT_GUPSHUP_TEMPLATE_FORM };
      this.v$.$reset();
      this.showCreateForm = true;
    },
    clearTemplateFormFields() {
      this.isEditing = false;
      this.currentTemplateId = null;
      this.currentTemplateChatwootId = null;
      this.formData = { ...DEFAULT_GUPSHUP_TEMPLATE_FORM };
      this.v$.$reset();
    },
    formatButtons() {
      if (this.formData.buttons) {
        try {
          // Validar se é um JSON válido
          JSON.parse(this.formData.buttons);
        } catch (error) {
          useAlert('Formato de botões inválido. Use JSON válido.');
          return false;
        }
      }
      return true;
    },
    async editTemplate(template) {
      // Preencher o formulário com os dados do template para edição
      console.log('Editando template:', template);

      // Extrair dados do containerMeta se disponível
      let containerData = {};
      let metaData = {};

      try {
        if (template.containerMeta) {
          containerData = JSON.parse(template.containerMeta);
        }
      } catch (e) {
        console.warn('Erro ao fazer parse do containerMeta:', e);
      }

      try {
        if (template.meta) {
          metaData = JSON.parse(template.meta);
        }
      } catch (e) {
        console.warn('Erro ao fazer parse do meta:', e);
      }

      this.formData = {
        elementName: template.elementName || '',
        languageCode: template.languageCode || 'pt_BR',
        category: template.category || 'UTILITY',
        templateType: template.templateType || 'TEXT',
        vertical: template.vertical || 'HEALTHCARE',
        content: containerData.data || template.data || '',
        footer: containerData.footer || '',
        buttons: containerData.buttons
          ? JSON.stringify(containerData.buttons, null, 2)
          : '',
        example: containerData.sampleText || metaData.example || '',
        enableSample: containerData.enableSample !== false,
      };

      console.log('Dados extraídos do containerMeta:', containerData);
      console.log('Dados extraídos do meta:', metaData);
      console.log('Formulário preenchido:', this.formData);
      this.isEditing = true;
      this.currentTemplateId = template.id;
      this.currentTemplateChatwootId = template.chatwootId || null;
      this.showCreateForm = true;
    },
    async deleteTemplate(template) {
      if (!this.ensureCredentialsConfigured()) {
        return;
      }
      if (
        confirm(
          `Tem certeza que deseja excluir o template "${template.elementName}"?`
        )
      ) {
        this.loading = true;
        try {
          // Usar o endpoint correto: /template/{elementName} (singular)
          const payload = {
            appId: this.getGupshupAppId(),
          };

          const response = await this.callGupshupAPI(
            'DELETE',
            `/template/${template.elementName}`,
            payload
          );
          if (response.status !== 'success') {
            throw new Error(response.message || 'Erro ao excluir template');
          }

          if (template.chatwootId) {
            try {
              await GupshupTemplatesAPI.destroy(
                this.inbox.id,
                template.chatwootId
              );
            } catch (storageError) {
              console.warn(
                'Erro ao remover template armazenado localmente:',
                storageError
              );
            }
          }

          useAlert('Template excluído com sucesso!');
          await this.fetchTemplates();
        } catch (error) {
          console.error('Erro ao excluir template:', error);
          console.error('Detalhes do erro:', {
            message: error.message,
            status: error.status,
            response: error.response,
            stack: error.stack,
          });
          useAlert(
            'Erro ao excluir template: ' +
              (error.message || 'Ocorreu um erro desconhecido.')
          );
        } finally {
          this.loading = false;
        }
      }
    },
    async updateTemplate() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      this.loading = true;
      try {
        // Usar o templateId armazenado durante a edição
        if (!this.currentTemplateId) {
          throw new Error('ID do template não encontrado para edição');
        }

        // Preparar payload para edição (apenas campos que podem ser editados)
        const payload = {
          appId: this.getGupshupAppId(),
        };

        // Adicionar apenas campos não vazios
        if (this.formData.content && this.formData.content.trim()) {
          payload.content = this.formData.content.trim();
        }

        if (this.formData.footer && this.formData.footer.trim()) {
          payload.footer = this.formData.footer.trim();
        }

        if (this.formData.example && this.formData.example.trim()) {
          payload.example = this.formData.example.trim();
        }

        // Adicionar botões apenas se fornecidos e válidos
        if (this.formData.buttons && this.formData.buttons.trim()) {
          try {
            const buttonsJson = JSON.parse(this.formData.buttons); // Validar JSON
            // Enviar botões como JSON compacto (sem quebras de linha)
            payload.buttons = JSON.stringify(buttonsJson);
          } catch (error) {
            throw new Error('Formato de botões inválido. Use JSON válido.');
          }
        }

        // Verificar se pelo menos um campo foi fornecido para edição
        const fieldsToUpdate = Object.keys(payload).filter(
          key => key !== 'appId'
        );
        if (fieldsToUpdate.length === 0) {
          throw new Error(
            'Pelo menos um campo deve ser fornecido para edição (content, footer, example ou buttons)'
          );
        }

        console.log('Payload para edição:', payload);
        console.log('Campos a serem atualizados:', fieldsToUpdate);
        console.log(
          'Template original - categoria:',
          this.templates.find(t => t.id === this.currentTemplateId)?.category
        );
        console.log(
          'Template original - oldCategory:',
          this.templates.find(t => t.id === this.currentTemplateId)?.oldCategory
        );

        // TESTE: Tentar editar apenas o conteúdo primeiro
        const testPayload = {
          appId: this.getGupshupAppId(),
          content: payload.content,
        };

        console.log('TESTE - Payload apenas com content:', testPayload);

        // Para atualização, usamos PUT com o templateId
        const response = await this.callGupshupAPI(
          'PUT',
          `/templates/${this.currentTemplateId}`,
          testPayload
        );
        if (response.status !== 'success') {
          throw new Error(response.message || 'Erro ao atualizar template');
        }

        await this.persistTemplateLocally({
          elementName: this.formData.elementName,
          templateId: this.currentTemplateId,
          chatwootId: this.currentTemplateChatwootId,
        });
        await this.fetchTemplates();

        useAlert('Template atualizado com sucesso!');
        this.resetForm();
      } catch (error) {
        console.error('Erro ao atualizar template:', error);
        console.error('Detalhes do erro:', {
          message: error.message,
          status: error.status,
          response: error.response,
          stack: error.stack,
        });
        useAlert(
          'Erro ao atualizar template: ' +
            (error.message || 'Ocorreu um erro desconhecido.')
        );
      } finally {
        this.loading = false;
      }
    },
  },
  validations: {
    formData: {
      elementName: { required },
      content: { required },
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="'Templates da Iungo'"
      :sub-title="'Gerencie os templates de mensagem da Iungo para este canal'"
      :show-border="false"
    >
      <div class="mb-4 flex flex-col gap-4">
        <div class="flex flex-col gap-2 md:flex-row md:items-end md:gap-4">
          <div class="flex-1">
            <woot-input
              v-model.trim="credentials.appId"
              label="App ID"
              placeholder="Informe o App ID da Iungo"
            />
          </div>
        </div>
        <div class="flex flex-col gap-2 md:flex-row md:items-end md:gap-4">
          <div class="flex-1">
            <woot-input
              v-model.trim="credentials.token"
              label="Token"
              type="password"
              placeholder="Informe o Token (Authorization) da Iungo"
            />
          </div>
          <div class="flex-1">
            <woot-input
              v-model.trim="credentials.accountApiKey"
              label="Account API Key"
              type="password"
              placeholder="Informe o Account API Key da Iungo"
            />
          </div>
        </div>
        <div class="flex flex-wrap gap-2 md:justify-end">
          <woot-button
            variant="smooth"
            :loading="connecting"
            :disabled="!canConnect || connecting"
            @click="connectGupshup"
          >
            {{ isConnected ? 'Atualizar conexão' : 'Conectar' }}
          </woot-button>
          <woot-button
            v-if="
              isConnected ||
              credentials.appId ||
              credentials.token ||
              credentials.accountApiKey
            "
            variant="clear"
            color-scheme="alert"
            :loading="disconnecting"
            :disabled="disconnecting"
            @click="disconnectGupshup"
          >
            Desconectar
          </woot-button>
        </div>
      </div>

      <div
        class="mb-6 flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300"
      >
        <span
          class="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium"
          :class="connectionStatusClass"
        >
          <span
            class="h-2 w-2 rounded-full"
            :class="connectionStatusDotClass"
          />
          {{ connectionStatusLabel }}
        </span>
        <span v-if="connectionState === 'pending'">
          Há alterações pendentes. Clique em <strong>Conectar</strong> para
          atualizar a integração.
        </span>
        <span v-else-if="isConnected">
          Você pode sincronizar novamente clicando em
          <strong>Atualizar conexão</strong>.
        </span>
        <span v-else>
          Preencha as credenciais e clique em <strong>Conectar</strong> para
          sincronizar os templates.
        </span>
      </div>

      <div
        v-if="hasConnectedMetadata"
        class="mb-6 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-200"
      >
        <p
          class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          Detalhes da conexão
        </p>
        <div class="flex flex-col gap-1">
          <!--          <span v-if="connectedAppName"><strong>App:</strong> {{ connectedAppName }}</span> -->
          <span v-if="connectedPhoneDisplay"
            ><strong>Telefone:</strong> {{ connectedPhoneDisplay }}</span
          >
        </div>
      </div>

      <div
        class="mb-6 rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200"
      >
        <p
          class="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          Automação do canal
        </p>
        <div class="flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <woot-switch
              v-model="iungoSettings.sendTypingIndicator"
              :disabled="savingIungoSettings"
              @input="updateIungoSettings"
            />
            <label>Mostrar “digitando…” para o cliente</label>
          </div>
          <div class="flex items-center gap-2">
            <woot-switch
              v-model="iungoSettings.autoMarkRead"
              :disabled="savingIungoSettings"
              @input="updateIungoSettings"
            />
            <label>Marcar mensagens como lidas automaticamente</label>
          </div>
        </div>
      </div>

      <!--<div
        v-if="webhookUrl"
        class="mb-6 rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200"
      >
        <p
          class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          Endpoint do webhook
        </p>
        <woot-code :script="webhookUrl" lang="bash" />
        <p class="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Este endpoint é registrado automaticamente usando a API SetSubscription
          v3 da Iungo. Caso seja necessário reconfigurar o webhook, clique em
          <strong>Atualizar conexão</strong> para reenviar os dados.
        </p>
      </div> -->

      <div
        v-if="!isConnected"
        class="mb-4 rounded border border-amber-300 bg-amber-50 p-4 text-sm text-amber-700 dark:border-amber-500 dark:bg-amber-900/20 dark:text-amber-200"
      >
        <template v-if="connectionState === 'pending'">
          <span v-if="hasPendingCredentials">
            Credenciais editadas. Clique em <strong>Conectar</strong> para
            aplicar as alterações e sincronizar novamente os templates.
          </span>
          <span v-else>
            Preencha o <strong>App ID</strong> e o
            <strong>API Key</strong> completos antes de conectar.
          </span>
        </template>
        <template v-else>
          Informe o <strong>App ID</strong> e o <strong>API Key</strong> da
          Iungo acima e clique em <strong>"Conectar"</strong> para habilitar o
          gerenciamento de templates.
        </template>
      </div> 

      <div class="mb-6 flex gap-2">
        <woot-button
          variant="smooth"
          color-scheme="primary"
          :disabled="!isConnected"
          @click="openCreateTemplateModal"
        >
          Criar Novo Template
        </woot-button>
        <!--  <woot-button
          variant="clear"
          :disabled="!isConnected"
          @click="testApiConnection"
        >
          Testar Conexão API
        </woot-button>
        <woot-button
          variant="clear"
          :disabled="!isConnected"
          @click="createTemplateWithoutAdjustment"
        >
          Criar Sem Ajustes
        </woot-button>  
        <woot-button variant="clear" :disabled="!isConnected" @click="createSimpleTemplate">
          Criar Template Simples
        </woot-button> -->
      </div>

      <!-- Formulário de criação / edição (modal) -->
      <woot-modal
        v-model:show="showCreateForm"
        :on-close="resetForm"
        size="medium"
      >
        <div
          class="modal-content max-h-[85vh] overflow-y-auto px-6 pb-6 pt-10"
        >
          <woot-modal-header
            :header-title="
              isEditing ? 'Editar Template' : 'Criar Novo Template'
            "
          />

        <div class="space-y-5">
          <section
            class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 space-y-3 shadow-sm"
          >
            <header>
              <h4
                class="text-sm font-semibold text-slate-700 dark:text-slate-100"
              >
                Nome do Elemento <span class="text-rose-500">*</span>
              </h4>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                Use letras minúsculas, números e underlines (ex:
                confirmacao_agendamento).
              </p>
            </header>
            <woot-input
              v-model.trim="formData.elementName"
              class="w-full"
              :class="{ error: v$.formData.elementName.$error }"
              placeholder="Ex: confirmacao_agendamento"
              :error="
                v$.formData.elementName.$error
                  ? 'Nome do elemento é obrigatório'
                  : ''
              "
              @blur="v$.formData.elementName.$touch"
            />
          </section>

          <section
            class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 space-y-3 shadow-sm"
          >
            <header>
              <h4
                class="text-sm font-semibold text-slate-700 dark:text-slate-100"
              >
                Código do Idioma <span class="text-rose-500">*</span>
              </h4>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                Informe o código no formato ISO (ex: pt_BR, en_US, es_MX).
              </p>
            </header>
            <woot-input
              v-model.trim="formData.languageCode"
              class="w-full"
              placeholder="pt_BR"
            />
          </section>

          <section
            class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 space-y-4 shadow-sm"
          >
            <header>
              <h4
                class="text-sm font-semibold text-slate-700 dark:text-slate-100"
              >
                Categoria <span class="text-rose-500">*</span>
              </h4>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                Escolha o objetivo principal da mensagem.
              </p>
            </header>
            <div class="grid gap-3 md:grid-cols-3">
              <label
                v-for="cat in categories"
                :key="cat.value"
                class="cursor-pointer rounded-lg border px-4 py-3 shadow-sm transition-colors focus-within:ring-2 focus-within:ring-n-brand"
                :class="
                  formData.category === cat.value
                    ? 'border-n-brand bg-n-brand/10 ring-2 ring-n-brand dark:bg-n-brand/20'
                    : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800'
                "
              >
                <div class="flex items-start gap-3">
                  <input
                    class="mt-1 h-4 w-4 shrink-0 accent-n-brand focus:ring-2 focus:ring-n-brand focus:ring-offset-0 dark:focus:ring-offset-slate-900"
                    type="radio"
                    name="gupshup-template-category"
                    :value="cat.value"
                    v-model="formData.category"
                  />
                  <div>
                    <p
                      class="text-sm font-semibold text-slate-700 dark:text-slate-100"
                    >
                      {{ cat.label }}
                    </p>
                    <p
                      class="text-xs text-slate-500 mt-1 dark:text-slate-400"
                      v-if="cat.value === 'MARKETING'"
                    >
                      Promoções, campanhas e mensagens de engajamento.
                    </p>
                    <p
                      class="text-xs text-slate-500 mt-1 dark:text-slate-400"
                      v-else-if="cat.value === 'UTILITY'"
                    >
                      Mensagens transacionais, confirmações e alertas.
                    </p>
                    <p
                      class="text-xs text-slate-500 mt-1 dark:text-slate-400"
                      v-else
                    >
                      Autenticação e verificação para seus clientes.
                    </p>
                  </div>
                </div>
              </label>
            </div>
          </section>

          <section
            class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 space-y-3 shadow-sm"
          >
            <h4
              class="text-sm font-semibold text-slate-700 dark:text-slate-100"
            >
              Tipo de Template <span class="text-rose-500">*</span>
            </h4>
            <select
              v-model="formData.templateType"
              class="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 shadow-sm focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            >
              <option
                v-for="type in templateTypes"
                :key="type.value"
                :value="type.value"
              >
                {{ type.label }}
              </option>
            </select>
          </section>

          <section
            class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 space-y-3 shadow-sm"
          >
            <h4
              class="text-sm font-semibold text-slate-700 dark:text-slate-100"
            >
              Vertical <span class="text-rose-500">*</span>
            </h4>
            <select
              v-model="formData.vertical"
              class="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 shadow-sm focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            >
              <option
                v-for="vertical in verticals"
                :key="vertical.value"
                :value="vertical.value"
              >
                {{ vertical.label }}
              </option>
            </select>
          </section>

          <section
            class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 space-y-3 shadow-sm"
          >
            <header>
              <h4
                class="text-sm font-semibold text-slate-700 dark:text-slate-100"
              >
                Conteúdo do Template <span class="text-rose-500">*</span>
              </h4>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                Utilize variáveis {{ 1 }}, {{ 2 }}, etc. para personalizar a
                mensagem.
              </p>
            </header>
            <div>
              <textarea
                v-model.trim="formData.content"
                class="w-full rounded-md border border-slate-300 px-3 py-2 shadow-sm focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                :class="{ error: v$.formData.content.$error }"
                rows="5"
                :placeholder="'Olá, seu agendamento para o dia {{1}} às {{2}} foi confirmado.'"
                @blur="v$.formData.content.$touch"
              ></textarea>
              <p
                v-if="v$.formData.content.$error"
                class="mt-1 text-xs text-rose-500"
              >
                Conteúdo é obrigatório
              </p>
            </div>
          </section>

          <section
            class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 space-y-3 shadow-sm"
          >
            <header>
              <h4
                class="text-sm font-semibold text-slate-700 dark:text-slate-100"
              >
                Exemplo
              </h4>
            </header>
            <woot-input
              v-model.trim="formData.example"
              class="w-full"
              placeholder="Olá, seu agendamento para o dia 25/11 às 14:00 foi confirmado."
            />
          </section>
        </div>
        <!--
         <div class="mt-4">
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Botões (JSON - Opcional)
          </label>
          <textarea
            v-model="formData.buttons"
            class="w-full rounded-md border border-slate-300 px-3 py-2 shadow-sm focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            rows="3"
            placeholder='[{"type":"QUICK_REPLY","text":"Mudar Data/Hora"}]'
          />
          <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Formato JSON para botões de resposta rápida. Deixe vazio se não quiser botões.
          </p>
        </div> -->

        <div class="bg-white dark:bg-slate-900 px-5 py-4">
          <label class="flex items-start gap-3 cursor-pointer">
            <input
              id="enableSample"
              v-model="formData.enableSample"
              type="checkbox"
              class="mt-1 h-4 w-4 rounded text-n-brand focus:ring-n-brand focus:ring-offset-0"
            />
            <div>
              <p
                class="text-sm font-semibold text-slate-700 dark:text-slate-100"
              >
                Habilitar exemplo
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Inclua exemplos para facilitar a avaliação e aprovação do
                template pela Iungo.
              </p>
            </div>
          </label>
        </div>

        <div class="mt-6 flex gap-2">
          <woot-button
            variant="smooth"
            color-scheme="primary"
            :loading="loading"
            :disabled="!isConnected"
            @click="isEditing ? updateTemplate() : createTemplate()"
          >
            {{ isEditing ? 'Atualizar Template' : 'Criar Template' }}
          </woot-button>
          <woot-button variant="clear" @click="clearTemplateFormFields">
            Limpar
          </woot-button>
        </div>
        </div>
      </woot-modal>

      <!-- Lista de templates -->
      <div v-if="templates.length > 0" class="mt-6">
        <h3 class="text-lg font-semibold mb-4">Templates Existentes</h3>
        <div class="space-y-4">
          <div
            v-for="template in templates"
            :key="template.id"
            class="border border-slate-200 dark:border-slate-700 rounded-lg p-4"
          >
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <h4 class="font-medium">{{ template.elementName }}</h4>
                <p class="text-sm text-slate-600 dark:text-slate-400">
                  {{ translatedCategory(template.category) }}
                  - {{ translatedTemplateType(template.templateType) }} -
                  {{ translatedLanguage(template.languageCode) }}
                </p>
                <p class="text-sm mt-2 text-slate-700 dark:text-slate-300">
                  {{ template.data || template.content }}
                </p>
                <div class="flex gap-2 mt-2">
                  <span
                    class="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 rounded"
                  >
                    Status: {{ translatedStatus(template.status) }}
                  </span>
                  <span
                    v-if="template.quality"
                    class="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 rounded"
                  >
                    Qualidade: {{ translatedQuality(template.quality) }}
                  </span>
                </div>
              </div>
              <div class="flex gap-2">
                <woot-button
                  variant="clear"
                  size="small"
                  :disabled="!isConnected"
                  @click="editTemplate(template)"
                >
                  Editar
                </woot-button>
                <woot-button
                  variant="clear"
                  color-scheme="alert"
                  size="small"
                  :disabled="!isConnected"
                  @click="deleteTemplate(template)"
                >
                  Excluir
                </woot-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else-if="!loading"
        class="text-center py-8 text-slate-500 dark:text-slate-400"
      >
        Nenhum template encontrado. Crie seu primeiro template acima.
      </div>

      <div v-if="loading" class="text-center py-8">
        <woot-spinner />
      </div>
    </SettingsSection>
  </div>
</template>
